import React, { useEffect, useState, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./SectionEditorDropdown.module.css";
import NumberInput from "../NumberInput";
import Button from "../Button";
import SelectColorInput from "../SelectColorInput";
import SelectNumberInput from "../SelectNumberInput";
import classNames from "classnames";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAlignLeft } from "@fortawesome/free-solid-svg-icons";
import { faArrowsAltH } from "@fortawesome/free-solid-svg-icons";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons";
import TextEditor from "../TextEditor";
import SelectDividerDropdown from "../SelectDividerDropdown";
import SelectImageDropdown from "../SelectImageDropdown";
import _ from "lodash";

// import DefaultImage from "../../assests/home-feed-image-coming-soon.jpg";
import SelectMediaDropdown from "../SelectMediaDropdown";
import CMSDropdown from "../CMSDropdown";
import BSMDropdown from "../BSMDropdown";

import convertVariableColor from "NewsAdmin/utils/convertVariableColor";
import SelectPhotoModal from "../SelectPhotoModal";
import SectionElementBar from "../SectionElementBar";

const defaultCMSItem = {
  contentId: 84,
  description: "",
  title: "Phenotypes",
  files: [
    {
      downloadPath:
        "https://view.vzaar.com/4d53ba9b12f59cf5f2b51bbdbda3bf6b/download?uss_token=3.202508192031.e0938a68097586eca38f154a8cb32e5a",
      fileId: 771,
      format: "Video",
      path: "4d53ba9b12f59cf5f2b51bbdbda3bf6b",
    },
  ],
};

/**
 * An editor that allows the user to add/edit section content for a feed item.
 * Even if the section is closed (done), open section elements will remain open and editable.
 */

// Add in props
export function SectionEditorDropdown({
  onClone,
  onMove,
  onDelete,
  onSectionUp,
  onSectionDown,
  isHidden,
  onHide,
  length,
  index,
  section,
  setSectionContent,
  setSectionBackgroundColor,
  editing,
  setEdit,
  setSections,
  sections,
}) {
  const [value, setValue] = useState(section.index);
  useEffect(() => {
    setValue(section.index);
  }, [section]);
  const [text, setText] = useState("NEW TEXT ELEMENT");

  // Normalize font sizes for 'TextEditor'
  const normalizeSize = (size) => {
    const validSizes = ["Small", "Normal", "Large", "Header2", "Header1"];
    return validSizes.includes(size) ? size : "Normal";
  };

  // Normalize image sizes for 'TextEditor'
  const normalizeWidth = (value) => {
    const allowed = ["Wide-xs", "Wide-sm", "Wide-md", "Wide-lg", "Normal"];
    const normalized = value?.charAt(0)?.toUpperCase() + value?.slice(1);
    return allowed.includes(normalized) ? normalized : "Normal";
  };

  const getAlignmentFromHtml = (html) => {
    if (!html || typeof html !== "string") return null;
    const match = html.match(/text-align\s*:\s*(left|center|right)/i);
    return match?.[1]?.toLowerCase() ?? null;
  };

  const getTextEditorSettings = (item) => {
    const savedSettings = item?.settings ?? {};
    const inferredAlignment = getAlignmentFromHtml(item?.text);

    return {
      alignment:
        inferredAlignment ??
        savedSettings.alignment ??
        savedSettings.align ??
        item?.alignment ??
        "left",
      customColor: savedSettings.customColor ?? item?.customColor ?? "#fff",
      size: normalizeSize(savedSettings.size ?? item?.size ?? "Normal"),
      textColor: savedSettings.textColor ?? item?.textColor ?? "default #000",
      width: normalizeWidth(savedSettings.width ?? item?.width ?? "Normal"),
    };
  };

  const getOpacityFromColor = (color) => {
    // Update for safe '.match()' handling
    const match =
      typeof color === "string" && color.match
        ? color.match(/rgba?\(([^)]+)\)/)
        : null;
    if (match && match[1]) {
      // Extract the alpha value (last component)
      const parts = match[1].split(",").map((p) => p.trim());
      if (parts.length === 4) {
        return parts[3];
      }
      // If rgb (no alpha), return default opacity
      return "1.00";
    }
    return "1.00";
  };

  const [numValue, setNumValue] = useState(
    getOpacityFromColor(section.backgroundColor)
  );

  //Default settings passed into a new element
  const [settings, setSettings] = useState({
    textColor: "--color-b #000",
    customColor: "#fff",
    alignment: "left",
    size: "Normal",
    width: "Normal",
  });

  const [selected, setSelected] = useState("");

  // const [editing, setEditing] = useState(parseInt(edit) == index ? true : false);
  const [hidden, setHidden] = useState(isHidden);

  const handleShowHide = () => {
    onHide(section.index, !hidden);
    setHidden(!hidden);
  };

  const handleValue = (e) => setValue(e.target.value - 1);

  // const isOpen = false;
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState("#fff");

  const getRGBFromColor = (color) => {
    // Update for safe '.match()' handling
    const match =
      typeof color === "string" && color.match
        ? color.match(/rgba?\(([^)]+)\)/)
        : null;
    if (match && match[1]) {
      // match[1] contains "r,g,b" or "r,g,b,a"
      // Extract just the RGB values (first 3 components)
      const parts = match[1].split(",").map((p) => p.trim());
      if (parts.length >= 3) {
        return `${parts[0]},${parts[1]},${parts[2]}`;
      }
    }
    return "";
  };

  const backgroundColor = useMemo(() => {
    const rgb = getRGBFromColor(section.backgroundColor);
    return rgb;
  }, [section.backgroundColor]);
  const currentColor = useMemo(
    () => "rgb(" + backgroundColor + ")",
    [backgroundColor]
  );

  // Track previous rgba string
  const prevBackgroundRef = useRef();

  useEffect(() => {
    const newValue = getOpacityFromColor(section.backgroundColor);
    if (prevBackgroundRef.current !== newValue) {
      setNumValue(newValue);
      prevBackgroundRef.current = newValue;
    }
  }, [section.backgroundColor]);

  // Guard against rendering loops
  const prevRgbaRef = useRef();
  const prevSectionRef = useRef();

  // Create a stable rgba string
  const rgbaValue = useMemo(() => {
    // backgroundColor already contains just "r,g,b" from getRGBFromColor
    // numValue contains the opacity
    if (!backgroundColor) {
      return "rgba(0,0,0,1.00)";
    }

    return `rgba(${backgroundColor},${numValue})`;
  }, [backgroundColor, numValue]);

  useEffect(() => {
    const sectionUnchanged =
      prevSectionRef.current?.index === section.index &&
      JSON.stringify(prevSectionRef.current?.backgroundColor) ===
        JSON.stringify(section.backgroundColor);

    if (rgbaValue === prevRgbaRef.current && sectionUnchanged) {
      // console.log("No update - section and rgba unchanged.");
      return;
    }

    setSectionBackgroundColor(section.index, rgbaValue);
    prevRgbaRef.current = rgbaValue;
    prevSectionRef.current = {
      index: section.index,
      backgroundColor: section.backgroundColor,
    };
  }, [rgbaValue, section]);

  const setColors = (e) => {
    const styleColor = e.target.style.backgroundColor; // e.g., "rgb(34, 45, 200)"
    const colorText = `${e.target.title} ${styleColor}`; // "Blue rgb(...)"

    const rgba = styleColor.replace("rgb(", "").replace(")", "").trim();

    // Update the parent with the new color
    setSectionBackgroundColor(section.index, `rgba(${rgba},${numValue})`);

    // This remains UI-only, safe to keep
    // console.log("User selected color:", colorText);
  };

  const handleNumValueChange = (e) => {
    setNumValue(e.target.value);
  };

  // figure out setting the divider color for each one.
  const dividerColor = "default #21242D";
  const [isDividerOpen, setIsDividerOpen] = useState(false);

  const isDev =
    window.location.hostname.includes("dev") ||
    window.location.hostname.includes("localhost");

  const defaultImageUrl = isDev
    ? "https://wwgusdev.blob.core.windows.net/home-feed/home-feed-image-coming-soon.jpg"
    : "https://wwgus.blob.core.windows.net/icons/home-feed-image-coming-soon.jpg";

  const [selectedPhoto, setSelectedPhoto] = useState({
    src: defaultImageUrl,
    alignment: "left",
    link: "",
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [contents, setContents] = useState(section.elements ?? []);
  useEffect(() => {
    setContents(section.elements ?? []);
  }, [section]);

  // Helper for looping guards
  const maybeUpdateSection = (newContents) => {
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      setSectionContent(section.index, newContents);
    }
  };

  const mediaSettings = {
    header: "NEW MEDIA",
    headerColor: "default #728C9F",
    id: "",
    buttonColor: "default",
    format: "Audio",
  };

  const addElement = (type) => {
    setSelected(type);

    let newElement = {};

    if (type === "image") {
      newElement = {
        type: "image",
        isOpen: false,
        currentPhoto: {
          src: defaultImageUrl,
          alignment: "center",
          link: "",
        },
      };
    } else if (type === "divider") {
      newElement = {
        type: "divider",
        isOpen: false,
        dividerColor: dividerColor,
      };
    } else if (type === "media") {
      newElement = {
        type: "media",
        isOpen: false,
        mediaSettings: mediaSettings,
      };
    } else if (type === "cms") {
      newElement = {
        type: "cms",
        isOpen: false,
        cmsItem: defaultCMSItem,
      };
    } else if (type === "bsm") {
      newElement = {
        type: "bsm",
        isOpen: false,
        bsmItem: [],
      };
    } else if (type === "text") {
      newElement = {
        type: "text",
        isOpen: false,
        text: text,
        settings: settings,
      };
    }

    let newContents = [...contents, newElement];

    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const setIsItemOpen = (index) => {
    let newContents = [...contents];
    newContents[index].isOpen = !newContents[index].isOpen;
    setContents(newContents);
  };

  const setDividerColor = (color, index) => {
    let newContents = [...contents];
    newContents[index].dividerColor = color;
    setContents(newContents);
  };

  const setMediaSettings = (field, value, index) => {
    let newContents = [...contents];
    newContents[index].mediaSettings[field] = value;
    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const setCmsItem = (item, index) => {
    let newContents = [...contents];
    // Replace (don't mutate) the element — the old object is shared with
    // section.elements, so mutating it defeats the change-detection guard
    // and the update never propagates up (no save button).
    newContents[index] = { ...newContents[index], cmsItem: item };
    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const setBsmItem = (item, index) => {
    let newContents = [...contents];
    // Replace (don't mutate) the element — see setCmsItem note above.
    newContents[index] = { ...newContents[index], bsmItem: item };
    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const onRemove = (index) => {
    let newContents = [
      ...contents.slice(0, index),
      ...contents.slice(index + 1),
    ];
    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const onMoveUp = (index) => {
    let newContents = [...contents];

    var element = newContents[index];
    newContents.splice(index, 1);
    newContents.splice(index - 1, 0, element);

    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const onMoveDown = (index) => {
    let newContents = [...contents];

    var element = newContents[index];
    newContents.splice(index, 1);
    newContents.splice(index + 1, 0, element);

    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  // const [selectedPhoto, setSelectedPhoto] = useState(currentPhoto.src || "");
  const [activeImageModalIndex, setActiveImageModalIndex] = useState(null);

  const openImageModal = (itemIndex, currentPhoto) => {
    setSelectedPhoto({
      src: currentPhoto?.src || defaultImageUrl,
      align: currentPhoto?.align || currentPhoto?.alignment || "center",
      alignment: currentPhoto?.alignment || "center",
      link: currentPhoto?.link || "",
    });
    setActiveImageModalIndex(itemIndex);
  };

  const handleSetPhoto = (photoSrc) => {
    const oldPhoto = { ...selectedPhoto };
    setSelectedPhoto({ ...oldPhoto, src: photoSrc });
  };

  const handleModalSubmit = (photoSrc, index) => {
    setActiveImageModalIndex(null);
    const existingPhotoSrc =
      contents[index]?.currentPhoto?.src || defaultImageUrl;
    const nextPhotoSrc = photoSrc || selectedPhoto.src || existingPhotoSrc;

    if (nextPhotoSrc === existingPhotoSrc) {
      return;
    }

    const newContents = contents.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            currentPhoto: {
              ...item.currentPhoto,
              src: nextPhotoSrc,
            },
          }
        : item
    );
    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const handleSubmit = (alignValue, linkValue, index) => {
    const existingPhoto = contents[index]?.currentPhoto || {};
    const existingAlignment =
      existingPhoto.align || existingPhoto.alignment || "center";
    const existingLink = existingPhoto.link || "";
    const isPhotoMetaChanged =
      existingAlignment !== alignValue || existingLink !== linkValue;

    if (!isPhotoMetaChanged) {
      const closedContents = contents.map((item, itemIndex) =>
        itemIndex === index ? { ...item, isOpen: false } : item
      );
      setActiveImageModalIndex(null);
      setContents(closedContents);
      return;
    }

    const newContents = contents.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            isOpen: false,
            currentPhoto: {
              ...item.currentPhoto,
              align: alignValue,
              alignment: alignValue,
              link: linkValue,
            },
          }
        : item
    );
    const newPhoto = {
      src: newContents[index]?.currentPhoto?.src ?? defaultImageUrl,
      align: alignValue,
      alignment: alignValue,
      link: linkValue,
    };

    setActiveImageModalIndex(null);

    newContents[index].currentPhoto = newPhoto;
    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  const onCloseModal = () => {
    setActiveImageModalIndex(null);
  };

  const onSetTextAndSettings = (text, settings, index) => {
    const normalizedSettings = {
      alignment: settings.alignment,
      size: normalizeSize(settings.size),
      width: normalizeWidth(settings.width),
      customColor: settings.customColor,
      textColor: settings.textColor,
    };

    const newContents = contents.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            text,
            ...normalizedSettings,
            settings: {
              ...(item.settings ?? {}),
              ...normalizedSettings,
            },
          }
        : item
    );
    setContents(newContents);
    // Guard against rendering loops
    if (JSON.stringify(section.elements) !== JSON.stringify(newContents)) {
      maybeUpdateSection(newContents);
    }
  };

  return (
    <>
      {!editing ? (
        <div>
          <div
            className={classNames(
              styles.container,
              hidden ? styles.hidden : ""
            )}
          >
            <div className={styles.flex}>
              <NumberInput
                maximum={length}
                inputLabel={"Section"}
                value={value}
                setValue={setValue}
              />
              <Button
                text={"Move"}
                onClick={() => {
                  onMove(parseInt(section.index), parseInt(value));
                }}
              />
              &nbsp;
              <Button
                className={styles.caret}
                img={<FontAwesomeIcon icon={faCaretUp} />} // Update to pass 'icon' instead of 'img'
                onClick={() => onSectionUp(index)}
                disabled={index == 0 ? true : false}
              />
              &nbsp;
              <Button
                className={styles.caret}
                img={<FontAwesomeIcon icon={faCaretDown} />} // Update to pass 'icon' instead of 'img'
                // need to add something to tell if it's last
                onClick={() => onSectionDown(index)}
                disabled={index + 1 == length ? true : false}
              />
              &nbsp;
              <Button
                className={styles.btnStyled}
                text={"Edit"}
                onClick={() => {
                  setEdit(index);
                }}
              />
              &nbsp;
              <Button
                className={styles.btnStyled}
                text={"Clone"}
                onClick={() => onClone(index)}
              />
              &nbsp;
              <Button
                className={styles.btnStyled}
                text={hidden ? "Show" : "Hide"}
                onClick={() => handleShowHide()}
              />
            </div>
          </div>
          {contents &&
            contents.map((item, index) => (
              <div
                key={`section-element-${index}`} // Add key prop
                className={styles.sectionElement}
                style={{
                  backgroundColor:
                    "rgba(" + backgroundColor + "," + numValue + ")",
                }}
              >
                <SelectPhotoModal
                  isVisible={activeImageModalIndex === index}
                  setIsVisible={onCloseModal}
                  currentPhotoSrc={
                    selectedPhoto?.src ?? item?.currentPhoto?.src ?? ""
                  }
                  setCurrentPhotoSrc={handleSetPhoto}
                  onSubmit={handleModalSubmit}
                  index={index}
                  disableOnErrorMsg={false}
                  minWidth={null}
                  minHeight={null}
                />

                {item.type == "text" ? (
                  <>
                    <TextEditor
                      initialContent={item.text}
                      settings={getTextEditorSettings(item)}
                      onClick={(_text, _settings) => {
                        onSetTextAndSettings(_text, _settings, index);
                      }}
                    />
                  </>
                ) : (
                  ""
                )}

                {item.type == "image" ? (
                  <>
                    <SelectImageDropdown
                      isOpen={item.isOpen}
                      setIsOpen={setIsItemOpen}
                      currentPhoto={
                        item.currentPhoto
                          ? item.currentPhoto
                          : {
                              src: item.path,
                              alignment: item.alignment,
                              link: item.link,
                            }
                      }
                      setCurrentPhoto={setSelectedPhoto}
                      index={index}
                      setIsModalOpen={openImageModal}
                      handleSubmit={handleSubmit}
                    />
                  </>
                ) : (
                  ""
                )}

                {item.type == "divider" ? (
                  <SelectDividerDropdown
                    dividerColor={
                      item.dividerColor ? item.dividerColor : "black"
                    }
                    setDividerColor={setDividerColor}
                    isOpen={item.isOpen}
                    setIsOpen={setIsItemOpen}
                    index={index}
                  />
                ) : (
                  ""
                )}

                {item.type == "media" ? (
                  <SelectMediaDropdown
                    mediaSettings={item.mediaSettings}
                    setMediaSettings={setMediaSettings}
                    isOpen={item.isOpen}
                    setIsOpen={setIsItemOpen}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    index={index}
                  />
                ) : (
                  ""
                )}

                {item.type == "cms" ? (
                  <CMSDropdown
                    cmsItem={item.cmsItem}
                    setCmsItem={(cmsItem) => setCmsItem(cmsItem, index)}
                  />
                ) : (
                  ""
                )}

                {item.type == "bsm" ? (
                  <BSMDropdown
                    bsmItem={item.bsmItem}
                    setBsmItem={(bsmItem) => setBsmItem(bsmItem, index)}
                  />
                ) : (
                  ""
                )}
              </div>
            ))}
        </div>
      ) : (
        <div>
          <div
            className={classNames(
              styles.containerTwo,
              hidden ? styles.hidden : ""
            )}
          >
            <div className={styles.spaceBetween}>
              <div className={styles.flex}>
                <NumberInput
                  maximum={length}
                  inputLabel={"Section"}
                  value={value}
                  setValue={setValue}
                />

                <Button
                  text={"Move"}
                  onClick={() =>
                    onMove(parseInt(section.index), parseInt(value))
                  }
                />
              </div>

              <div className={styles.flex}>
                <SelectColorInput
                  currentColor={currentColor}
                  setCurrentColor={(e) => setColors(e)}
                  customColor={customColor}
                  setCustomColor={(value) => setCustomColor(value)}
                  isOpen={isOpen}
                  setIsOpen={() => setIsOpen(!isOpen)}
                />
                &nbsp;
                <SelectNumberInput
                  value={numValue}
                  onSelect={handleNumValueChange}
                  numberOptions={[
                    "0.00",
                    "0.05",
                    "0.10",
                    "0.15",
                    "0.20",
                    "0.25",
                    "0.30",
                    "0.35",
                    "0.40",
                    "0.45",
                    "0.50",
                    "0.55",
                    "0.60",
                    "0.65",
                    "0.70",
                    "0.75",
                    "0.80",
                    "0.85",
                    "0.90",
                    "0.95",
                    "1.00",
                  ]}
                />
                &nbsp;
                <Button
                  className={styles.btnLight}
                  text={""}
                  onClick={() => onSectionUp(index)}
                  img={<FontAwesomeIcon icon={faCaretUp} />}
                  disabled={index == 0 ? true : false}
                />
                <Button
                  className={styles.btnLight}
                  text={""}
                  onClick={() => onSectionDown(index)}
                  img={<FontAwesomeIcon icon={faCaretDown} />}
                  disabled={index + 1 == length ? true : false}
                />
                &nbsp;
                <Button
                  className={styles.btnDelete}
                  text={"Delete"}
                  onClick={() => onDelete(index)}
                />
                &nbsp;
                <Button text={"Done"} onClick={() => setEdit(null)} />
              </div>
            </div>

            <div className={styles.middleContainer}>
              {contents &&
                contents.map((item, index) => (
                  <div
                    key={`section-element-${index}`} // Add key prop
                    className={styles.sectionElement}
                    style={{
                      backgroundColor:
                        "rgba(" + backgroundColor + ", " + numValue + ")",
                    }}
                  >
                    <SelectPhotoModal
                      isVisible={activeImageModalIndex === index}
                      setIsVisible={onCloseModal}
                      currentPhotoSrc={
                        selectedPhoto?.src ?? item?.currentPhoto?.src ?? ""
                      }
                      setCurrentPhotoSrc={handleSetPhoto}
                      onSubmit={handleModalSubmit}
                      index={index}
                      disableOnErrorMsg={false}
                      minWidth={null}
                      minHeight={null}
                    />
                    <SectionElementBar
                      onRemove={() => onRemove(index)}
                      position={
                        index == 0 ? 1 : index !== contents.length - 1 ? 2 : 3
                      }
                      onUp={() => onMoveUp(index)}
                      onDown={() => onMoveDown(index)}
                    />

                    {item.type == "text" ? (
                      <>
                        <TextEditor
                          initialContent={item.text}
                          settings={getTextEditorSettings(item)}
                          onClick={(_text, _settings) => {
                            onSetTextAndSettings(_text, _settings, index);
                          }}
                        />
                      </>
                    ) : (
                      ""
                    )}

                    {item.type == "image" ? (
                      <>
                        <SelectImageDropdown
                          isOpen={item.isOpen}
                          setIsOpen={setIsItemOpen}
                          currentPhoto={
                            item.currentPhoto
                              ? item.currentPhoto
                              : {
                                  src: item.path,
                                  alignment: item.alignment,
                                  link: item.link,
                                }
                          }
                          setCurrentPhoto={setSelectedPhoto}
                          index={index}
                          setIsModalOpen={openImageModal}
                          handleSubmit={handleSubmit}
                        />
                      </>
                    ) : (
                      ""
                    )}

                    {item.type == "divider" ? (
                      <SelectDividerDropdown
                        dividerColor={
                          item.dividerColor ? item.dividerColor : "black"
                        }
                        setDividerColor={setDividerColor}
                        isOpen={item.isOpen}
                        setIsOpen={setIsItemOpen}
                        index={index}
                      />
                    ) : (
                      ""
                    )}

                    {item.type == "media" ? (
                      <SelectMediaDropdown
                        mediaSettings={item.mediaSettings}
                        setMediaSettings={setMediaSettings}
                        isOpen={item.isOpen}
                        setIsOpen={setIsItemOpen}
                        isModalOpen={isModalOpen}
                        setIsModalOpen={setIsModalOpen}
                        index={index}
                      />
                    ) : (
                      ""
                    )}

                    {item.type == "cms" ? (
                      <CMSDropdown
                        cmsItem={item.cmsItem}
                        setCmsItem={(cmsItem) => setCmsItem(cmsItem, index)}
                      />
                    ) : (
                      ""
                    )}

                    {item.type == "bsm" ? (
                      <BSMDropdown
                        bsmItem={item.bsmItem}
                        setBsmItem={(bsmItem) => setBsmItem(bsmItem, index)}
                      />
                    ) : (
                      ""
                    )}
                  </div>
                ))}
            </div>

            <div className={classNames(styles.footer)}>
              <div className={styles.add}> Add:</div>
              <Button
                className={styles.btnLight}
                text={"Text"}
                onClick={() => addElement("text")}
                img={
                  <FontAwesomeIcon
                    icon={faAlignLeft}
                    style={{
                      paddingRight: "5px",
                    }}
                  />
                }
              />

              <Button
                className={styles.btnLight}
                text={"Divider"}
                onClick={() => addElement("divider")}
                img={
                  <FontAwesomeIcon
                    icon={faArrowsAltH}
                    style={{
                      paddingRight: "5px",
                    }}
                  />
                }
              />
              <Button
                className={styles.btnLight}
                text={"Image"}
                onClick={() => addElement("image")}
                img={
                  <FontAwesomeIcon
                    icon={faImage}
                    style={{
                      paddingRight: "5px",
                    }}
                  />
                }
              />
              <Button
                className={styles.btnLight}
                text={"Media"}
                onClick={() => addElement("media")}
                img={
                  <FontAwesomeIcon
                    icon={faPlay}
                    style={{
                      paddingRight: "5px",
                    }}
                  />
                }
              />
              <Button
                className={styles.btnLight}
                text={"CMS"}
                onClick={() => addElement("cms")}
                img={
                  <FontAwesomeIcon
                    icon={faFile}
                    style={{
                      paddingRight: "5px",
                    }}
                  />
                }
              />
              <Button
                className={styles.btnLight}
                text={"BSM"}
                onClick={() => addElement("bsm")}
                img={
                  <FontAwesomeIcon
                    icon={faAlignLeft}
                    style={{
                      paddingRight: "5px",
                    }}
                  />
                }
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

SectionEditorDropdown.propTypes = {
  /**
   * Function that fires when clone button is clicked.
   */
  onClone: PropTypes.func,
  /**
   * 	Function that fires when the move button is clicked.  * Uses the sectionPosition prop.
   */
  onMove: PropTypes.func,
  /**
   * Function that fires when the up button is clicked.  * Disabled if the sectionPosition is first.
   */
  onSectionUp: PropTypes.func,
  /**
   * Function that fires when the down button clicked.  * Disabled if the sectionPosition is last.
   */
  onSectionDown: PropTypes.func,
  /**
   * Boolean that determines if the section is hidden in the feed item.
   */
  isHidden: PropTypes.bool,
  /**
   * Shows the length of all section editor dropdowns. Disables move down if position is equal to length
   */
  length: PropTypes.number,
  /**
   * Shows the index for and item to set certain information for it
   */
  index: PropTypes.number,
  /**
   * Displays the information for the section (Position, sections, index)
   */
  section: PropTypes.object,
};
